<template>
  <a-modal
    :visible="visible"
    :title="title || $t('SKU快捷查询')"
    width="900px"
    :dialog-style="{ top: '20px' }"
    @cancel="handleCancel"
    :footer="null"
    :maskClosable="false"
  >
    <a-form-model :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
      <a-row style="margin-bottom: 16px">
        <a-col :span="24">
          <a-form-model-item
            :label="$t('IMEI/SN')"
            :label-col="{ span: 3 }"
            :wrapper-col="{ span: 21 }"
          >
            <a-input
              v-model="imeiSn"
              :placeholder="$t('输入 IMEI/SN 自动查询库存')"
              allow-clear
              :suffix="imeiSnLoading ? '' : ''"
              @change="handleImeiSnChange"
            >
              <a-icon v-if="imeiSnLoading" slot="suffix" type="loading" />
            </a-input>
          </a-form-model-item>
        </a-col>
      </a-row>

      <div class="imei-divider">
        <span class="imei-divider__text">{{ $t('或') }}</span>
      </div>

      <a-row v-if="recentPairs.length" style="margin-bottom: 20px">
        <a-col :span="24">
          <div class="recent-wrapper" style="display: flex; align-items: center; flex-wrap: wrap">
            <div class="recent-label">{{ $t('历史选择') }}：</div>
            <div class="recent-tags">
              <a-tag
                v-for="item in recentPairs"
                :key="item.key"
                closable
                @close="(e) => handleRemoveRecent(e, item.key)"
                @click="() => applyRecent(item)"
              >
                {{ item.brandName }} / {{ item.modelName }}
              </a-tag>
            </div>
          </div>
        </a-col>
      </a-row>

      <a-row :gutter="16" style="margin-top: 20px">
        <a-col :span="12">
          <a-form-model-item>
            <template slot="label">
              {{ $t('品牌') }}
              <a-icon
                type="sync"
                class="text-link"
                style="margin-left: 4px; padding-right: 4px"
                :spin="brandLoading"
                @click="handleRefreshBrandList"
              />
            </template>

            <a-spin :spinning="brandLoading" size="small">
              <div class="tag-group tag-group-scroll">
                <span v-if="!brandOptions.length" style="color: rgba(0, 0, 0, 0.45)">
                  {{ $t('暂无品牌数据') }}
                </span>
                <a-checkable-tag
                  class="cursor-pointer"
                  v-for="item in brandOptions"
                  :key="item.value"
                  :checked="form.brandCode === item.value"
                  :disabled="brandLoading"
                  @change="(checked) => handleBrandTagChange(checked, item.value)"
                >
                  {{ item.label }}
                </a-checkable-tag>
              </div>
            </a-spin>
          </a-form-model-item>
        </a-col>

        <a-col :span="12">
          <a-form-model-item :label="$t('型号')">
            <a-select
              style="width: 100%"
              v-model="form.modelCode"
              :placeholder="$t('请先选品牌，再选择型号')"
              allow-clear
              show-search
              :disabled="!form.brandCode || skuRelationLoading"
              :filterOption="
                (input, option) =>
                  (option.componentOptions?.children?.[0]?.text || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
              "
              @change="handleModelChange"
            >
              <a-select-option v-for="item in modelOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-spin
        :spinning="skuRelationLoading"
        :tip="$t('加载中')"
        :class="['sku-relation-spin', { 'sku-relation-spin--loading': skuRelationLoading }]"
      >
        <a-row
          v-if="specFields.length"
          :gutter="16"
          style="margin-top: 20px; display: flex; flex-wrap: wrap; gap: 20px 0"
        >
          <a-col v-for="f in specFields" :key="f.styleType" :span="12">
            <a-form-model-item :label="f.styleType">
              <div class="tag-group">
                <a-checkable-tag
                  v-for="v in f.styleValues"
                  class="cursor-pointer"
                  :key="`${f.styleType}:${v}`"
                  :checked="selectedSpecs?.[f.styleType] === v"
                  :disabled="skuRelationLoading"
                  @change="(checked) => handleSpecTagChange(checked, f.styleType, v)"
                >
                  {{ v }}
                </a-checkable-tag>
              </div>
            </a-form-model-item>
          </a-col>
        </a-row>

        <div v-if="matchHint" style="margin-top: 10px; color: rgba(0, 0, 0, 0.65)">
          {{ matchHint }}
        </div>
      </a-spin>

      <a-row style="margin-top: 20px">
        <a-col :span="24" style="text-align: right">
          <a-button @click="handleCancel">{{ $t('取消') }}</a-button>
          <a-button
            type="primary"
            style="margin-left: 8px"
            :disabled="!canSubmit"
            @click="emitIfReady(true)"
          >
            {{ $t('确定') }}
          </a-button>
        </a-col>
      </a-row>
    </a-form-model>
  </a-modal>
</template>

<script>
import api from '@/service/api'
import http from '@/service/axios'

// 仅作为"优先展示/排序"的规格集合；最终必选项以接口返回 specOptions 为准
// 支持中英文双语，接口可能根据语言返回不同 styleType 名称
const PREFERRED_STYLE_TYPES_ZH = ['颜色', '运行内存', '机身存储', '成新', '版本']
const PREFERRED_STYLE_TYPES_EN = ['Color', 'RAM', 'Storage', 'Condition', 'Version']
const PREFERRED_STYLE_TYPES_ALL = [...PREFERRED_STYLE_TYPES_ZH, ...PREFERRED_STYLE_TYPES_EN]
// 排序优先级（中英文各自顺序保持一致，实际只有一套会匹配到）
const PREFERRED_STYLE_TYPES_ORDER = [...PREFERRED_STYLE_TYPES_ZH, ...PREFERRED_STYLE_TYPES_EN]
const RECENT_CACHE_KEY = 'scm_sku_quick_query_recent_pairs_v1'
const BRAND_CACHE_KEY = 'scm_sku_quick_query_brand_options_v1'
const RECENT_MAX = 12

export default {
  name: 'SkuQuickQueryModal',
  props: {
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      visible: false,
      skuRelationLoading: false,
      brandLoading: false,
      brandOptions: [],
      modelOptions: [],
      form: {
        brandCode: undefined,
        modelCode: undefined,
      },
      selectedSpecs: {},
      skuResult: null,
      matchHint: '',
      recentPairs: [],
      imeiSn: '',
      imeiSnLoading: false,
      // eslint-disable-next-line vue/no-reserved-keys
      _imeiSnTimer: null,
    }
  },
  computed: {
    selectedBrandRaw() {
      return this.brandOptions.find((b) => b.value === this.form.brandCode)?.raw || null
    },
    selectedModelRaw() {
      return this.modelOptions.find((m) => m.value === this.form.modelCode)?.raw || null
    },
    specFields() {
      const specOptions = this.skuResult?.specOptions || []
      const filtered = specOptions
        .filter((o) => PREFERRED_STYLE_TYPES_ALL.includes(o.styleType))
        .map((o) => ({
          styleType: o.styleType,
          styleValues: Array.isArray(o.styleValues) ? o.styleValues : [],
        }))
      // 固定顺序（中英文各自顺序一致，实际只有一套会匹配到）
      return PREFERRED_STYLE_TYPES_ORDER.map((t) => filtered.find((x) => x.styleType === t)).filter(
        Boolean
      )
    },
    requiredStyleTypes() {
      // 以接口 specOptions 实际返回为准（避免某些型号没有“运行内存”等字段）
      return (this.specFields || []).map((x) => x.styleType)
    },
    canSubmit() {
      if (!this.skuResult) return false
      return this.requiredStyleTypes.every((t) => !!this.selectedSpecs?.[t])
    },
  },
  methods: {
    open({ brandCode, modelCode } = {}) {
      this.resetAll()
      this.visible = true
      this.loadRecentPairs()
      this.fetchBrandList().then(() => {
        if (brandCode) {
          this.form.brandCode = brandCode
          this.handleBrandChange(brandCode)
        }
        if (modelCode) {
          this.form.modelCode = modelCode
          this.handleModelChange(modelCode)
        }
      })
    },
    handleCancel() {
      this.visible = false
      this.$emit('cancel')
    },
    resetAll() {
      this.skuRelationLoading = false
      this.brandLoading = false
      this.brandOptions = []
      this.modelOptions = []
      this.form = { brandCode: undefined, modelCode: undefined }
      this.selectedSpecs = {}
      this.skuResult = null
      this.matchHint = ''
      this.imeiSn = ''
      this.imeiSnLoading = false
      if (this._imeiSnTimer) clearTimeout(this._imeiSnTimer)
      this._imeiSnTimer = null
      // recentPairs 不在这里清空，避免 modal 内操作时闪烁
    },
    resetSpecs() {
      this.selectedSpecs = {}
      this.skuResult = null
      this.matchHint = ''
    },
    fetchBrandList() {
      return this.fetchBrandListWithCache()
    },
    parseBrandListToOptions(brandList) {
      return (brandList || []).map((b) => ({
        label: b.brandName,
        value: b.brandId,
        raw: b,
      }))
    },
    getBrandListFromCache() {
      try {
        const raw = localStorage.getItem(BRAND_CACHE_KEY)
        const brandList = raw ? JSON.parse(raw) : []
        return Array.isArray(brandList) ? brandList : []
      } catch (e) {
        return []
      }
    },
    setBrandListCache(brandList) {
      try {
        localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brandList || []))
      } catch (e) {
        // ignore
      }
    },
    fetchBrandListWithCache({ forceRefresh = false } = {}) {
      if (!forceRefresh) {
        const cache = this.getBrandListFromCache()
        if (cache.length) {
          this.brandOptions = this.parseBrandListToOptions(cache)
          return Promise.resolve()
        }
      }
      this.brandLoading = true
      return new Promise((resolve) => {
        http({
          type: 'post',
          url: api.getUPDBrandList,
          data: {},
          hasLoading: false,
          success: (res) => {
            const brandList = res?.result?.brandList || []
            this.brandOptions = this.parseBrandListToOptions(brandList)
            this.setBrandListCache(brandList)
            this.brandLoading = false
            resolve()
          },
          fail: () => {
            this.brandLoading = false
            resolve()
          },
        })
      })
    },
    handleRefreshBrandList() {
      this.fetchBrandListWithCache({ forceRefresh: true })
    },
    fetchModelListByBrandCode(brandCode) {
      const brandRaw = this.brandOptions.find((b) => b.value === brandCode)?.raw
      const models = brandRaw?.brandModelList || []
      this.modelOptions = models.map((m) => ({
        label: m.modelName,
        value: m.modelCode,
        raw: m,
      }))
    },
    handleBrandChange(brandCode) {
      this.form.modelCode = undefined
      this.modelOptions = []
      this.resetSpecs()
      if (!brandCode) return
      this.fetchModelListByBrandCode(brandCode)
    },
    handleBrandTagChange(checked, brandCode) {
      // 单选：checked=false 代表取消选中
      const next = checked ? brandCode : undefined
      this.form.brandCode = next
      this.handleBrandChange(next)
    },
    handleModelChange(modelCode) {
      this.resetSpecs()
      if (!modelCode) return
      const modelName =
        this.modelOptions.find((m) => m.value === modelCode)?.raw?.modelName ||
        this.modelOptions.find((m) => m.value === modelCode)?.label
      if (!modelName) return
      this.querySkuRelationMap(modelCode, modelName)
    },
    querySkuRelationMap(modelCode, modelName) {
      this.skuRelationLoading = true
      http({
        type: 'post',
        url: api.querySkuRelationMap,
        data: { modelCode, modelName },
        hasLoading: false,
        success: (res) => {
          this.skuResult = res?.result || null
          this.skuRelationLoading = false
          this.autoPickSingles()
          this.emitIfReady(false)
        },
        fail: () => {
          this.skuRelationLoading = false
        },
      })
    },
    autoPickSingles() {
      const next = { ...(this.selectedSpecs || {}) }
      for (const f of this.specFields) {
        const values = f.styleValues || []
        if (values.length === 1) next[f.styleType] = values[0]
      }
      this.selectedSpecs = next
      this.updateMatchHint()
    },
    handleSpecTagChange(checked, styleType, value) {
      const next = { ...(this.selectedSpecs || {}) }
      next[styleType] = checked ? value : undefined
      this.selectedSpecs = next
      this.updateMatchHint()
      // this.emitIfReady(false)
    },
    updateMatchHint() {
      if (!this.skuResult) {
        this.matchHint = ''
        return
      }
      const matched = this.findMatchedSkuRelations()
      if (!this.canSubmit) {
        this.matchHint = matched.length
          ? `${this.$t('当前可匹配SKU数')}: ${matched.length}`
          : this.$t('当前条件下无可匹配SKU')
        return
      }
      if (matched.length === 1) {
        this.matchHint = `${this.$t('已匹配到SKU')}: ${matched[0].skuCode}`
      } else if (matched.length === 0) {
        this.matchHint = this.$t('当前条件下无可匹配SKU')
      } else {
        this.matchHint = `${this.$t('匹配到多个SKU，请继续调整条件')} (${matched.length})`
      }
    },
    findMatchedSkuRelations() {
      const relations = this.skuResult?.skuRelations || []
      const selected = this.selectedSpecs || {}
      return relations.filter((r) => {
        const specs = r?.specs || []
        // 仅按已选择的字段过滤（支持未选齐时做 hint）
        return Object.keys(selected).every((styleType) => {
          const v = selected[styleType]
          if (!v) return true
          return specs.some((s) => s.styleType === styleType && s.styleValue === v)
        })
      })
    },
    emitIfReady(force) {
      if (!this.skuResult) return
      if (!force && !this.canSubmit) return
      const required = this.requiredStyleTypes

      const matched = this.findMatchedSkuRelations().filter((r) => {
        // force / canSubmit 都要求必选项齐全，额外再做一次严格校验
        return required.every((t) => {
          const v = this.selectedSpecs?.[t]
          return (r?.specs || []).some((s) => s.styleType === t && s.styleValue === v)
        })
      })

      if (matched.length !== 1) {
        this.updateMatchHint()
        if (force) this.$message.warning(this.$t('请先选择完整规格并确保唯一SKU'))
        return
      }

      const skuCode = matched[0].skuCode
      const payload = {
        skuCode,
        goodsId: this.skuResult.goodsId,
        goodsCode: this.skuResult.goodsCode,
        goodsName: this.skuResult.goodsName,
        brandName: this.skuResult.brandName,
        modelName: this.skuResult.modelName,
        selectedSpecs: { ...(this.selectedSpecs || {}) },
        raw: this.skuResult,
      }
      this.saveRecentPair({
        brandCode: this.form.brandCode,
        brandName: this.selectedBrandRaw?.brandName || this.skuResult.brandName,
        modelCode: this.form.modelCode,
        modelName: this.selectedModelRaw?.modelName || this.skuResult.modelName,
      })
      this.$emit('selected', payload)
      this.visible = false
    },

    loadRecentPairs() {
      try {
        const raw = localStorage.getItem(RECENT_CACHE_KEY)
        const list = raw ? JSON.parse(raw) : []
        this.recentPairs = Array.isArray(list) ? list : []
      } catch (e) {
        this.recentPairs = []
      }
    },
    persistRecentPairs() {
      try {
        localStorage.setItem(RECENT_CACHE_KEY, JSON.stringify(this.recentPairs || []))
      } catch (e) {
        // ignore
      }
    },
    saveRecentPair({ brandCode, brandName, modelCode, modelName }) {
      if (!brandCode || !modelCode) return
      const key = `${brandCode}__${modelCode}`
      const now = Date.now()
      const next = [
        {
          key,
          brandCode,
          brandName: brandName || '',
          modelCode,
          modelName: modelName || '',
          ts: now,
        },
        ...(this.recentPairs || []).filter((x) => x && x.key !== key),
      ].slice(0, RECENT_MAX)
      this.recentPairs = next
      this.persistRecentPairs()
    },
    handleRemoveRecent(e, key) {
      // 防止 close 冒泡触发 click 选中
      try {
        e && e.preventDefault && e.preventDefault()
        e && e.stopPropagation && e.stopPropagation()
      } catch (err) {
        // ignore
      }
      this.recentPairs = (this.recentPairs || []).filter((x) => x && x.key !== key)
      this.persistRecentPairs()
    },
    applyRecent(item) {
      if (!item?.brandCode || !item?.modelCode) return
      this.fetchBrandList().then(() => {
        this.form.brandCode = item.brandCode
        this.handleBrandChange(item.brandCode)
        this.$nextTick(() => {
          this.form.modelCode = item.modelCode
          this.handleModelChange(item.modelCode)
        })
      })
    },
    handleImeiSnChange() {
      if (this._imeiSnTimer) clearTimeout(this._imeiSnTimer)
      const val = (this.imeiSn || '').trim()
      if (!val) return
      this._imeiSnTimer = setTimeout(() => {
        this.queryByImeiSn(val)
      }, 500)
    },
    queryByImeiSn(imei) {
      this.imeiSnLoading = true
      http({
        type: 'post',
        url: api.queryStockListSimple,
        data: { pageNo: 1, pageSize: 1, imei },
        hasLoading: false,
        success: (res) => {
          this.imeiSnLoading = false
          const item = res?.result?.list?.[0]
          if (!item) {
            this.$message.warning(this.$t('未找到对应库存记录'))
            return
          }
          // 直接 emit selected，带上库存条目信息
          const payload = {
            skuCode: item.skuCode || item.barcode,
            skuName: item.skuName,
            productName: item.productName,
            inventoryItemId: item.inventoryItemId,
            barcode: item.barcode,
            unitPrice: item.unitPrice,
            selectedSpecs: item.styleMap || {},
            raw: item,
            _fromImeiSn: true,
          }
          console.log('payload', payload)
          if (!payload.skuCode) {
            this.$message.warning(this.$t('库存记录中未包含 SKU 编码，请手动选择'))
            return
          }
          this.$emit('selected', payload)
          this.visible = false
        },
        fail: () => {
          this.imeiSnLoading = false
        },
      })
    },
  },
}
</script>

<style scoped>
.recent-wrapper {
  display: flex;
  align-items: flex-start;
}
.recent-label {
  width: 84px;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.65);
}
.recent-tags {
  flex: 1;
}
.recent-tags :deep(.ant-tag) {
  cursor: pointer;
  user-select: none;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 4px;
}
.tag-group-scroll {
  max-height: 132px;
  overflow: auto;
  padding-right: 4px;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

/* 请求 relationMap 时内容可能尚未渲染，给 Spin 一个最小高度避免指示器贴顶不明显 */
.sku-relation-spin--loading {
  min-height: 120px;
}

.imei-divider {
  position: relative;
  display: flex;
  align-items: center;
  margin: 4px 0 16px;
  color: rgba(0, 0, 0, 0.35);
  font-size: 12px;
}
.imei-divider::before,
.imei-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e8e8e8;
}
.imei-divider__text {
  padding: 0 12px;
  letter-spacing: 2px;
}
</style>
